
import { v4 as uuidv4 } from 'uuid';

export interface AdminAction {
  id: string;
  timestamp: string;
  actionType: 'student_record' | 'document' | 'permission' | 'system_setting';
  description: string;
  performedBy: string;
  details: Record<string, any>;
}

interface AdminActionRecord {
  actionId: string;
  actions: AdminAction[];
}

// In-memory storage for admin actions (in a real app, this would be in a database)
const adminActionStore: Record<string, AdminActionRecord> = {};

// Add explicit export for trackStudentAction as an alias to trackStudentRecordModification
// to maintain backward compatibility
export const trackStudentAction = (
  studentId: number,
  modifiedBy: string,
  description: string,
  changes: Record<string, any>
) => {
  return trackStudentRecordModification(studentId, modifiedBy, changes, description);
};

export const trackStudentRecordModification = (
  studentId: number,
  modifiedBy: string,
  changes: Record<string, any>,
  description: string
) => {
  const action: AdminAction = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    actionType: 'student_record',
    description,
    performedBy: modifiedBy,
    details: {
      studentId,
      changes
    }
  };

  addAdminAction(studentId.toString(), action);
  return action;
};

export const trackDocumentAction = (
  documentId: string,
  status: 'uploaded' | 'approved' | 'rejected',
  handledBy: string,
  details: Record<string, any>
) => {
  const action: AdminAction = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    actionType: 'document',
    description: `Document ${status}`,
    performedBy: handledBy,
    details: {
      documentId,
      status,
      ...details
    }
  };

  addAdminAction(documentId, action);
  return action;
};

export const trackPermissionChange = (
  userId: string,
  changedBy: string,
  oldPermissions: string[],
  newPermissions: string[]
) => {
  const action: AdminAction = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    actionType: 'permission',
    description: 'Permission changes',
    performedBy: changedBy,
    details: {
      userId,
      oldPermissions,
      newPermissions
    }
  };

  addAdminAction(userId, action);
  return action;
};

export const trackSystemSettingChange = (
  settingKey: string,
  changedBy: string,
  oldValue: any,
  newValue: any
) => {
  const action: AdminAction = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    actionType: 'system_setting',
    description: `System setting "${settingKey}" modified`,
    performedBy: changedBy,
    details: {
      setting: settingKey,
      oldValue,
      newValue
    }
  };

  addAdminAction('system_settings', action);
  return action;
};

export const getAdminActions = (entityId: string): AdminAction[] => {
  return adminActionStore[entityId]?.actions || [];
};

// Helper function to add admin action
const addAdminAction = (entityId: string, action: AdminAction) => {
  if (!adminActionStore[entityId]) {
    adminActionStore[entityId] = {
      actionId: entityId,
      actions: []
    };
  }
  adminActionStore[entityId].actions.push(action);
};
